/**
 * Video History Popover -- Grid popover for browsing video generation history.
 *
 * Rendered inside the node's DOM subtree so it zooms/pans with the canvas.
 * Uses nopan/nodrag CSS classes to prevent ReactFlow from intercepting interactions.
 *
 * Follows the same positioning/styling as ImageHistoryPopover but adapts cells
 * to inline video players with play/pause, expand (fullscreen with audio),
 * and selection UI (star / "Choose vN").
 */

import { memo, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

export interface VideoHistoryEntry {
  user_prompt?: string
  result?: {
    id?: string
    local_path?: string
    r2_url?: string
    metadata?: Record<string, unknown>
  }
  refs?: Array<{ id: string; local_path?: string }>
  metadata?: Record<string, unknown>
}

interface VideoHistoryPopoverProps {
  buildVideoUrl: (localPath: string) => string | null
  history: VideoHistoryEntry[]
  pickedIndex: number
  onSelect: (index: number) => void
  isOpen: boolean
  batchPending?: number
}

const ROWS = 3
const CELL_WIDTH = 200
const GAP = 4
const PADDING = 8

const VideoCell = memo(function VideoCell({
  realIdx,
  versionNumber,
  isSelected,
  videoUrl,
  onSelect,
  onExpand,
  playSignal,
  pauseSignal,
  onPlayStateChange,
}: {
  realIdx: number
  versionNumber: number
  isSelected: boolean
  videoUrl: string | null
  onSelect: (index: number) => void
  onExpand: (url: string) => void
  playSignal: number
  pauseSignal: number
  onPlayStateChange: (realIdx: number, playing: boolean) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)

  // Respond to "Play All" signal
  useEffect(() => {
    if (playSignal > 0 && videoRef.current && videoUrl && !hasError) {
      videoRef.current.currentTime = 0
      videoRef.current.play().catch(() => {})
      setIsPlaying(true)
      onPlayStateChange(realIdx, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSignal])

  // Respond to "Pause All" signal
  useEffect(() => {
    if (pauseSignal > 0 && videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
      onPlayStateChange(realIdx, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseSignal])

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!videoRef.current || !videoUrl || hasError) return
    if (isPlaying) {
      videoRef.current.pause()
      setIsPlaying(false)
      onPlayStateChange(realIdx, false)
    } else {
      videoRef.current.play().catch(() => {})
      setIsPlaying(true)
      onPlayStateChange(realIdx, true)
    }
  }

  const handleEnded = () => {
    setIsPlaying(false)
    onPlayStateChange(realIdx, false)
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (videoUrl) onExpand(videoUrl)
  }

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onSelect(realIdx)
  }

  return (
    <div
      data-testid="video-history-cell"
      className={`relative rounded overflow-hidden border-2 transition-colors ${
        isSelected
          ? 'border-yellow-400/50'
          : 'border-transparent hover:border-border'
      }`}
      style={{ aspectRatio: '16/9' }}
    >
      {videoUrl && !hasError ? (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            onEnded={handleEnded}
            onError={() => setHasError(true)}
          />
          <button
            data-testid="video-cell-play-btn"
            onClick={handleTogglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
          >
            {isPlaying ? (
              <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-border">
                <svg
                  className="w-4 h-4 text-foreground/90"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-border">
                <svg
                  className="w-4 h-4 text-foreground/90 ml-0.5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            )}
          </button>
        </>
      ) : (
        <div className="w-full h-full bg-black/30 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-foreground/10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}

      {/* Selection UI */}
      <div className="absolute top-1 right-1 z-10">
        {isSelected ? (
          <span
            data-testid="video-cell-selected-star"
            className="text-yellow-400 text-sm leading-none"
            title="Currently selected"
          >
            &#9733;
          </span>
        ) : (
          <button
            data-testid="video-cell-choose-btn"
            onClick={handleSelectClick}
            className="text-xxs font-medium text-foreground bg-black/80 hover:bg-black/90 px-1.5 py-0.5 rounded transition-colors"
          >
            Choose v{versionNumber}
          </button>
        )}
      </div>

      {/* Expand button */}
      {videoUrl && !hasError && (
        <button
          data-testid="video-cell-expand-btn"
          onClick={handleExpandClick}
          className="absolute bottom-1 right-1 w-6 h-6 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors z-10"
          title="Expand fullscreen"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
            />
          </svg>
        </button>
      )}
    </div>
  )
})

VideoCell.displayName = 'VideoCell'

const VideoPendingCell = memo(function VideoPendingCell() {
  return (
    <div
      data-testid="video-pending-item"
      className="relative rounded overflow-hidden border-2 border-yellow-400/30 bg-black/20 flex items-center justify-center"
      style={{ aspectRatio: '16/9' }}
    >
      <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      <span className="absolute bottom-1 text-xxs text-foreground/30">
        Generating...
      </span>
    </div>
  )
})

VideoPendingCell.displayName = 'VideoPendingCell'

export const VideoHistoryPopover = memo(function VideoHistoryPopover({
  buildVideoUrl,
  history,
  pickedIndex,
  onSelect,
  isOpen,
  batchPending = 0,
}: VideoHistoryPopoverProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const [playSignal, setPlaySignal] = useState(0)
  const [pauseSignal, setPauseSignal] = useState(0)
  const [playingSet, setPlayingSet] = useState<Set<number>>(new Set())
  const [fullscreenVideoUrl, setFullscreenVideoUrl] = useState<string | null>(
    null
  )

  const totalItems = history.length + batchPending
  const columns = Math.min(totalItems <= 2 ? 2 : 3, Math.max(totalItems, 1))
  const itemsPerPage = columns * ROWS
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  type DisplayItem =
    | { kind: 'pending'; key: string }
    | { kind: 'entry'; entry: VideoHistoryEntry; realIdx: number }

  const displayItems = useMemo<DisplayItem[]>(() => {
    const items: DisplayItem[] = []
    for (let i = 0; i < batchPending; i++) {
      items.push({ kind: 'pending', key: `pending-${i}` })
    }
    for (let i = history.length - 1; i >= 0; i--) {
      items.push({ kind: 'entry', entry: history[i], realIdx: i })
    }
    return items
  }, [history, batchPending])

  const pageItems = useMemo(() => {
    const start = currentPage * itemsPerPage
    return displayItems.slice(start, start + itemsPerPage)
  }, [displayItems, currentPage, itemsPerPage])

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0)
    }
  }, [currentPage, totalPages])

  const isAnyPlaying = playingSet.size > 0

  const handlePlayStateChange = useCallback(
    (realIdx: number, playing: boolean) => {
      setPlayingSet((prev) => {
        const next = new Set(prev)
        if (playing) {
          next.add(realIdx)
        } else {
          next.delete(realIdx)
        }
        return next
      })
    },
    []
  )

  const handlePlayAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setPlaySignal((s) => s + 1)
  }

  const handlePauseAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setPauseSignal((s) => s + 1)
  }

  const handlePrevPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setPauseSignal((s) => s + 1)
    setPlayingSet(new Set())
    if (currentPage > 0) setCurrentPage(currentPage - 1)
  }

  const handleNextPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setPauseSignal((s) => s + 1)
    setPlayingSet(new Set())
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1)
  }

  const handleExpand = useCallback((url: string) => {
    setFullscreenVideoUrl(url)
  }, [])

  if (!isOpen) return null

  const popoverWidth =
    columns * CELL_WIDTH + (columns - 1) * GAP + PADDING * 2

  return (
    <>
      <div
        role="dialog"
        aria-label="Video generation history"
        data-testid="video-history-popover"
        className="nopan nodrag absolute z-50"
        style={{
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: 8,
          width: popoverWidth,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center px-2 py-1.5 border-b border-border">
            <button
              data-testid="video-history-play-all-btn"
              onClick={isAnyPlaying ? handlePauseAll : handlePlayAll}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-foreground/70 hover:text-foreground hover:bg-surface-2 transition-colors"
            >
              {isAnyPlaying ? (
                <>
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                  Pause All
                </>
              ) : (
                <>
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Play All
                </>
              )}
            </button>
          </div>

          {/* Grid */}
          <div
            className="p-2"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: `${GAP}px`,
            }}
          >
            {pageItems.map((item) => {
              if (item.kind === 'pending') {
                return <VideoPendingCell key={item.key} />
              }

              const { entry, realIdx } = item
              const isSelected = realIdx === pickedIndex
              const videoPath = entry.result?.local_path
              const videoUrl = videoPath ? buildVideoUrl(videoPath) : null
              const versionNumber = realIdx + 1

              return (
                <VideoCell
                  key={entry.result?.id || realIdx}
                  realIdx={realIdx}
                  versionNumber={versionNumber}
                  isSelected={isSelected}
                  videoUrl={videoUrl}
                  onSelect={onSelect}
                  onExpand={handleExpand}
                  playSignal={playSignal}
                  pauseSignal={pauseSignal}
                  onPlayStateChange={handlePlayStateChange}
                />
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-2 border-t border-border">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                aria-label="Previous page"
                aria-disabled={currentPage === 0}
                className={`p-1 rounded transition-colors ${
                  currentPage === 0
                    ? 'text-foreground/20 cursor-default'
                    : 'text-text-secondary hover:text-foreground hover:bg-surface-2'
                }`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className="text-xs text-foreground/50">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
                aria-label="Next page"
                aria-disabled={currentPage === totalPages - 1}
                className={`p-1 rounded transition-colors ${
                  currentPage === totalPages - 1
                    ? 'text-foreground/20 cursor-default'
                    : 'text-text-secondary hover:text-foreground hover:bg-surface-2'
                }`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Video Modal */}
      {fullscreenVideoUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setFullscreenVideoUrl(null)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setFullscreenVideoUrl(null)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={fullscreenVideoUrl}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

VideoHistoryPopover.displayName = 'VideoHistoryPopover'
